import i18n from '../i18n';
import {
  getAmplifyVisitorRegion,
  getCachedAmplifyVisitorRegion,
  type VisitorRegion,
} from './amplifyGeo';
import {
  getLanguageFromPath,
  isRouteLanguageCode,
  type RouteLanguageCode,
} from './languageRouting';

const MANUAL_LANGUAGE_STORAGE_KEY = 'jlbhk_manual_language_v1';
const AUTO_REGION_LANGUAGE_STORAGE_KEY = 'jlbhk_auto_region_language_v1';
const I18NEXT_LANGUAGE_STORAGE_KEY = 'i18nextLng';

function safeGetLocalStorage(key: string) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function getNavigatorLanguage() {
  if (typeof navigator === 'undefined') return 'hk';
  return normalizeSiteLanguage(navigator.languages?.[0] || navigator.language);
}

function getLanguageFromResolvedRegion(region: VisitorRegion): RouteLanguageCode | null {
  if (region.countryCode === 'HK') return 'hk';
  if (region.countryCode === 'CN' && region.isXinjiang) return 'ug';
  if (region.countryCode === 'CN') return 'cn';
  return null;
}

function getNonRegionalLanguage() {
  const previousAutoLanguage = safeGetLocalStorage(AUTO_REGION_LANGUAGE_STORAGE_KEY);
  const currentStoredLanguage = safeGetLocalStorage(I18NEXT_LANGUAGE_STORAGE_KEY);

  if (previousAutoLanguage && currentStoredLanguage === previousAutoLanguage) {
    return getNavigatorLanguage();
  }

  return normalizeSiteLanguage(i18n.resolvedLanguage || i18n.language);
}

export function normalizeSiteLanguage(value?: string | null): RouteLanguageCode {
  if (!value) return 'hk';

  const language = value.toLowerCase().replace('_', '-');

  if (isRouteLanguageCode(language)) return language;
  if (language.startsWith('vi')) return 'vn';
  if (language.startsWith('zh-cn') || language.startsWith('zh-hans')) return 'cn';
  if (language.startsWith('zh')) return 'hk';
  if (language.startsWith('ja')) return 'jp';
  if (language.startsWith('ko')) return 'kr';
  if (language.startsWith('th')) return 'th';
  if (language.startsWith('de')) return 'de';
  if (language.startsWith('fr')) return 'fr';
  if (language.startsWith('it')) return 'it';
  if (language.startsWith('es')) return 'es';
  if (language.startsWith('ug')) return 'ug';
  if (language.startsWith('en')) return 'en';

  return 'hk';
}

/**
 * Returns a language immediately only when the URL, a manual selection, or a cached
 * Amplify result already makes the current website language deterministic.
 */
export function getImmediateCurrentSiteLanguage(): RouteLanguageCode | null {
  if (typeof window === 'undefined') return null;

  const routeLanguage = getLanguageFromPath(window.location.pathname, __BASE_PATH__);
  if (routeLanguage) return routeLanguage;

  const manualLanguageSelected = safeGetLocalStorage(MANUAL_LANGUAGE_STORAGE_KEY) === '1';
  if (manualLanguageSelected) {
    return normalizeSiteLanguage(i18n.resolvedLanguage || i18n.language);
  }

  const cachedRegion = getCachedAmplifyVisitorRegion();
  if (!cachedRegion) return null;

  return getLanguageFromResolvedRegion(cachedRegion) || getNonRegionalLanguage();
}

/**
 * Resolves the same final language used by the website, including HK/CN region matching
 * and the browser-language restoration used for visitors outside those regions.
 */
export async function resolveCurrentSiteLanguage(): Promise<RouteLanguageCode> {
  const immediateLanguage = getImmediateCurrentSiteLanguage();
  if (immediateLanguage) return immediateLanguage;

  const region = await getAmplifyVisitorRegion();
  return getLanguageFromResolvedRegion(region) || getNonRegionalLanguage();
}
